import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Loader, Message } from 'semantic-ui-react';
import {
  getBuyerProfile,
  getBuyerProfileQuestions,
  saveBuyerProfileAnswers,
} from '~/api/buyerProfile';
import BuyerProfileForm from './BuyerProfileForm';
import { BuyerProfile, BuyerProfileAnswerEntry, QuestionConfig } from './types';

interface Props {
  /** Company whose profile to load. Passed to the API only for admins. */
  companyId: string;
  /** Admins may target another company; investors are pinned server-side. */
  isAdmin?: boolean;
}

const BuyerProfileTab: React.FC<Props> = ({ companyId, isAdmin }) => {
  const [config, setConfig] = useState<QuestionConfig | null>(null);
  const [profile, setProfile] = useState<BuyerProfile | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  // Only admins may pass a companyId; for investors the server uses the session.
  const targetCompanyId = isAdmin ? companyId : undefined;

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    Promise.all([getBuyerProfileQuestions(), getBuyerProfile(targetCompanyId)])
      .then(([cfg, prof]) => {
        if (cancelled) return;
        setConfig(cfg);
        setProfile(prof);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [targetCompanyId]);

  const handleSave = useCallback(
    async (answers: BuyerProfileAnswerEntry[]) => {
      const saved = await saveBuyerProfileAnswers(answers, targetCompanyId);
      return saved.answers ?? [];
    },
    [targetCompanyId]
  );

  if (status === 'loading') {
    return <Loader active inline="centered" content="Loading buyer profile…" />;
  }
  if (status === 'error' || !config || !profile) {
    return <Message negative>Could not load the buyer profile. Please try again.</Message>;
  }

  // A draft profile has not been through the guided wizard yet — offer it.
  // Admins viewing another company's profile don't get the onboarding CTA.
  const showOnboardingCta = !isAdmin && profile.status === 'draft';

  return (
    <>
      {showOnboardingCta && (
        <div className="card buyer-profile-onboarding-cta">
          <div className="buyer-profile-onboarding-cta-text">
            <h3>New here? Try the guided setup</h3>
            <p>
              We&apos;ll walk you through your buyer profile a few steps at a time.
              You can switch to this full form whenever you like.
            </p>
          </div>
          <Button primary as={Link} to="/buyer-profile/onboarding">
            Start guided setup
          </Button>
        </div>
      )}
      <BuyerProfileForm
        config={config}
        initialAnswers={profile.answers ?? []}
        onSave={handleSave}
      />
    </>
  );
};

export default BuyerProfileTab;
